/**
 * useVisualizerStepDetection — bidirectional visualizer attachment detection.
 *
 * Scans the pipeline and detects visualizer steps adjacent to real
 * (transformer/generator) steps. Attachment rules:
 *
 *   Visualizer IMMEDIATELY AFTER a real step  → real step's OUTPUT panel
 *   Visualizer IMMEDIATELY BEFORE a real step → real step's INPUT panel
 *
 * Each panel slot (input/output) can hold at most one visualizer.
 * Only the IMMEDIATE neighbor attaches — visualizers separated by other
 * visualizers are left unattached (standalone).
 *
 * Examples:
 *   [Tree] → [Format] → [Diff]
 *     → Format input = tree, Format output = diff
 *
 *   [Format] → [Tree]
 *     → Format output = tree
 *
 *   [A-real] → [Viz1] → [Viz2] → [B-real]
 *     → A output = Viz1, B input = Viz2
 *
 * @see docs/Reaserch/BackendAdvocacyDesignDoc.md §3
 */

import { useMemo } from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { IPipelineStep } from '@/interfaces/IPipeline';
import { isVisualizer, type ITool } from '@/interfaces/ITool';
import { getModeForToolId } from '@/lib/visualizerManifest';
import type { VisualizerMode } from '@/store/useVisualizerStore';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Which panel a visualizer is attached to on its host step. */
export type AttachedPanel = 'input' | 'output';

/** Rendering hint for a single pipeline step. */
export interface StepRenderHint {
  step: IPipelineStep;
  tool: ITool | undefined;
  isVisualizer: boolean;
  /**
   * If this visualizer is attached to an adjacent real step, describes
   * the attachment. Null for non-visualizers and standalone visualizers.
   */
  attachment: {
    hostStepId: string;
    panel: AttachedPanel;
    mode: VisualizerMode;
  } | null;
  /** Shorthand: true when attachment is non-null. */
  isAttached: boolean;
}

/** Override applied to a real step's IO panel. */
export interface PanelOverride {
  mode: VisualizerMode;
  visualizerStepId: string;
  visualizerTool: ITool;
}

export interface VisualizerDetectionResult {
  /** Render hints for every step in the pipeline. */
  hints: StepRenderHint[];
  /** stepId → PanelOverride for real steps whose INPUT panel is overridden. */
  inputOverrides: Map<string, PanelOverride>;
  /** stepId → PanelOverride for real steps whose OUTPUT panel is overridden. */
  outputOverrides: Map<string, PanelOverride>;
  /** Set of visualizer step IDs that are attached (render as compact chips). */
  attachedVisualizerIds: Set<string>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Analyze a pipeline for bidirectional visualizer attachment.
 *
 * Two-pass algorithm:
 *   Pass 1 — For each real step, if the NEXT step is a visualizer → output override
 *   Pass 2 — For each real step, if the PREV step is a visualizer (unclaimed) → input override
 */
export function detectVisualizerSteps(pipeline: IPipelineStep[]): VisualizerDetectionResult {
  const inputOverrides = new Map<string, PanelOverride>();
  const outputOverrides = new Map<string, PanelOverride>();
  const attachedVisualizerIds = new Set<string>();

  // Pre-resolve tools and viz flags
  const resolved = pipeline.map((step) => {
    const tool = ToolRegistry.get(step.toolId);
    return { step, tool, isViz: tool ? isVisualizer(tool) : false };
  });

  // Track which visualizer steps have been claimed
  const claimed = new Set<string>();

  // Pass 1: output overrides — real step's NEXT is a visualizer
  for (let i = 0; i < resolved.length; i++) {
    const cur = resolved[i];
    const next = resolved[i + 1];
    if (cur.isViz || !next || !next.isViz) continue;

    const mode = getModeForToolId(next.step.toolId);
    if (!mode || !next.tool) continue;

    outputOverrides.set(cur.step.id, {
      mode,
      visualizerStepId: next.step.id,
      visualizerTool: next.tool,
    });
    claimed.add(next.step.id);
    attachedVisualizerIds.add(next.step.id);
  }

  // Pass 2: input overrides — real step's PREV is an unclaimed visualizer
  for (let i = 0; i < resolved.length; i++) {
    const cur = resolved[i];
    const prev = resolved[i - 1];
    if (cur.isViz || !prev || !prev.isViz) continue;
    if (claimed.has(prev.step.id)) continue;

    const mode = getModeForToolId(prev.step.toolId);
    if (!mode || !prev.tool) continue;

    inputOverrides.set(cur.step.id, {
      mode,
      visualizerStepId: prev.step.id,
      visualizerTool: prev.tool,
    });
    claimed.add(prev.step.id);
    attachedVisualizerIds.add(prev.step.id);
  }

  // Build hints
  const hints: StepRenderHint[] = resolved.map(({ step, tool, isViz }) => {
    if (!isViz) {
      return { step, tool, isVisualizer: false, attachment: null, isAttached: false };
    }

    // Find which real step this visualizer is attached to
    let attachment: StepRenderHint['attachment'] = null;

    // Check output overrides — this viz is attached to some real step's output
    for (const [hostId, override] of outputOverrides) {
      if (override.visualizerStepId === step.id) {
        attachment = { hostStepId: hostId, panel: 'output', mode: override.mode };
        break;
      }
    }

    // Check input overrides
    if (!attachment) {
      for (const [hostId, override] of inputOverrides) {
        if (override.visualizerStepId === step.id) {
          attachment = { hostStepId: hostId, panel: 'input', mode: override.mode };
          break;
        }
      }
    }

    return {
      step,
      tool,
      isVisualizer: true,
      attachment,
      isAttached: attachment !== null,
    };
  });

  return { hints, inputOverrides, outputOverrides, attachedVisualizerIds };
}

/**
 * React hook wrapper. Recomputes only when the pipeline reference changes.
 */
export function useVisualizerStepDetection(pipeline: IPipelineStep[]): VisualizerDetectionResult {
  return useMemo(() => detectVisualizerSteps(pipeline), [pipeline]);
}
